import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Bell,
  Mail,
  Shield,
  MessageSquare,
  AlertTriangle,
  Loader2,
  CheckCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';

export function NotificationSettings() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile-notifications', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('lead_notifications_enabled, security_notifications_enabled')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: { lead_notifications_enabled?: boolean; security_notifications_enabled?: boolean }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Notification settings updated');
      queryClient.invalidateQueries({ queryKey: ['profile-notifications'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update settings');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Choose how and when you want to be notified
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Lead Notifications */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-full bg-primary/10">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
              <div>
                <Label className="text-base font-semibold">New Lead Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Get an email whenever someone submits a lead form on your QR landing pages
                </p>
              </div>
            </div>
            <Switch
              checked={profile?.lead_notifications_enabled ?? true}
              onCheckedChange={(checked) => updateSettings.mutate({ lead_notifications_enabled: checked })}
              disabled={updateSettings.isPending}
            />
          </div>

          {/* Security Notifications */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-full bg-amber-500/10">
                <Shield className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <Label className="text-base font-semibold">Security Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Receive emails for failed login attempts, geo-blocks, and suspicious activity
                </p>
              </div>
            </div>
            <Switch
              checked={profile?.security_notifications_enabled ?? true}
              onCheckedChange={(checked) => updateSettings.mutate({ security_notifications_enabled: checked })}
              disabled={updateSettings.isPending}
            />
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Delivery
            </h4>
            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/10 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-700">All set!</p>
                <p className="text-xs text-blue-600/80 mt-1">
                  Emails will be sent to <strong>{user?.email}</strong>. 
                  We use Resend for high-deliverability lead notifications.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Coming Soon</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 opacity-60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                📱
              </div>
              <span className="text-sm">Browser Push Notifications</span>
            </div>
            <Switch disabled />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                💬
              </div>
              <span className="text-sm">WhatsApp Notifications</span>
            </div>
            <Switch disabled />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
